import { useCallback, useEffect, useRef } from 'react';
import getCMResponse from '../../../utils/api_manager';

// Facebook Login (for Business) popup hook for the new /datasource/meta/*
// grant flow (see docs/api/endpoints/datasource.md, "Meta — Facebook Login
// (for Business)" section, added 2026-07-05). Unlike useMetaLogin.js (which
// builds the facebook.com OAuth dialog URL entirely client-side from
// Config.META_APP_ID and a client-generated `state`), this flow requires an
// async round trip to cq-api *before* the dialog URL is known:
//   1. GET /datasource/meta/oauth/initiate?project_id&redirect_uri —
//      cq-api generates the CSRF `state`, sets it as an httpOnly cookie, and
//      returns { redirect_url, state } (the facebook.com dialog URL).
//   2. Open a popup at `redirect_url`.
//   3. The popup lands on /oauth/meta/callback (see
//      pages/OAuthCallback/MetaOAuthCallback.jsx — shared, unmodified,
//      already generic over source/code/state/error), which postMessages
//      { code, state, error } back to this window and closes.
//   4. This hook resolves onSuccess({ code, redirect_uri, state }) — the
//      caller POSTs these plus project_id to /datasource/meta/oauth/callback,
//      which re-verifies `state` against its own cookie server-side.
//
// Because step 1 is an async network call, window.open() can't be invoked
// synchronously inside the click handler the way useMetaLogin.js does it —
// most browsers only allow popups opened synchronously within a user
// gesture's call stack. To avoid the popup being blocked, a blank popup is
// opened synchronously first and then navigated to `redirect_url` once the
// initiate call resolves (a standard workaround for "OAuth dialog URL comes
// from an API call" flows).
const META_OAUTH_MESSAGE_SOURCE = 'meta-oauth';

const getRedirectUri = () => `${window.location.origin}/oauth/meta/callback`;

const useMetaBusinessLogin = ({ projectId, onSuccess, onError, onClosed }) => {
  const popupRef = useRef(null);
  const listenerRef = useRef(null);
  const pollRef = useRef(null);
  const completedRef = useRef(false);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    if (listenerRef.current) {
      window.removeEventListener('message', listenerRef.current);
      listenerRef.current = null;
    }
    clearPoll();
  }, [clearPoll]);

  useEffect(() => cleanup, [cleanup]);

  const login = useCallback(() => {
    if (!projectId) {
      onError?.(new Error('No project selected — cannot start the Meta connection.'));
      return;
    }

    cleanup();
    completedRef.current = false;

    // Open synchronously (still inside the click handler's call stack) so
    // the browser treats it as gesture-initiated, then navigate it once we
    // know where it should go.
    popupRef.current = window.open('about:blank', 'meta-oauth', 'width=600,height=700');

    if (!popupRef.current) {
      onError?.(new Error('Meta OAuth popup was blocked by the browser.'));
      return;
    }

    const redirectUri = getRedirectUri();

    getCMResponse('/datasource/meta/oauth/initiate', 'GET', {
      project_id: projectId,
      redirect_uri: redirectUri,
    }).then((result) => {
      const { redirect_url: redirectUrl, state } = result?.data || {};

      if (!redirectUrl || !state) {
        popupRef.current?.close();
        onError?.(new Error('Meta did not return an authorization URL. Please try again.'));
        return;
      }

      if (popupRef.current.closed) {
        onError?.(new Error('The Meta authorization popup was closed before it could load.'));
        return;
      }

      popupRef.current.location.href = redirectUrl;

      // Poll for the popup being closed manually (no postMessage ever
      // arrives in that case). `.closed` stays readable cross-origin even
      // while the popup shows facebook.com, so this works without needing
      // any cooperation from the OAuth dialog itself.
      pollRef.current = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearPoll();
          if (!completedRef.current) {
            onClosed?.();
          }
        }
      }, 500);

      listenerRef.current = (event) => {
        if (event.origin !== window.location.origin) return;
        if (!event.data || event.data.source !== META_OAUTH_MESSAGE_SOURCE) return;
        // The dialog echoes back the server-generated `state` — only accept
        // messages matching the state we just received from /oauth/initiate,
        // so a stale/foreign popup can't be mistaken for this attempt.
        if (event.data.state !== state) return;

        completedRef.current = true;
        cleanup();
        popupRef.current?.closed === false && popupRef.current.close();

        if (event.data.code) {
          onSuccess?.({ code: event.data.code, redirect_uri: redirectUri, state });
        } else {
          onError?.(event.data.error || new Error('Meta OAuth did not return a code.'));
        }
      };

      window.addEventListener('message', listenerRef.current);
    }).catch((err) => {
      popupRef.current?.close();
      onError?.(err);
    });
  }, [cleanup, clearPoll, projectId, onSuccess, onError, onClosed]);

  return login;
};

export default useMetaBusinessLogin;
